import type { UserPermissions } from "@redotech/account-common/permissions";
import type { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import type { RedoProduct } from "@redotech/redo-common-model/redo-product";
import type { IntegrationKind } from "@redotech/redo-integrations-core";
import type { IntegrationStatusKey } from "@redotech/redo-merchant-integrations-rpc/schema/returns-rpc/get-all-integration-statuses";
import type { Gen2IntegrationKind } from "@redotech/redo-return-core-common/integrations/gen2-integration-kinds";
import type { StorefrontPlatform } from "@redotech/redo-storefront-common/storefront-config";
import type { CarrierBrandSlug } from "./carrier-brands";

export const IntegrationTypeCategory = {
  ECOMMERCE_PLATFORM: "ecommerce-platform",
  ERP_WMS_3PL: "erp-wms-3pl",
  CARRIER: "carrier",
  SMS_EMAIL_MARKETING: "sms-email-marketing",
  MESSAGING_CHANNEL: "messaging-channel",
  REVIEWS_LOYALTY_SUBSCRIPTIONS: "reviews-loyalty-subscriptions",
  RETURNS_SERVICE: "returns-service",
  HELPDESK: "helpdesk",
  PAYMENTS_FINANCE: "payments-finance",
  ANALYTICS: "analytics",
  CROSS_BORDER: "cross-border",
  OTHER: "other",
} as const;
export type IntegrationTypeCategory =
  (typeof IntegrationTypeCategory)[keyof typeof IntegrationTypeCategory];

/** Team-settings-derived connected checks resolved in use-catalog-statuses. */
export type TeamSettingKey =
  | "yofi"
  | "customerWidget"
  | "supportPhone"
  | "customSenderEmails";

/** Where the unified page resolves this integration's connected state. */
export type StatusSource =
  | { source: "statusKey"; key: IntegrationStatusKey }
  | { source: "gen3"; kind: IntegrationKind }
  | { source: "gen2Kind"; kind: Gen2IntegrationKind }
  /** Connected iff ANY of the kinds is connected (e.g. email = Gmail or Outlook). */
  | {
      source: "gen2AnyKind";
      kinds: readonly Gen2IntegrationKind[];
      /** Extra team-settings arm ORed in (e.g. custom sender emails). */
      orSetting?: TeamSettingKey;
    }
  /** Connected iff any carrier account (EasyPost-brokered or standalone) folds into the brand. */
  | { source: "carrier"; brand: CarrierBrandSlug }
  /** Connected iff the team's storefront platform matches. */
  | { source: "platform"; platform: StorefrontPlatform }
  /** Connected iff the named team-settings-derived capability is enabled. */
  | { source: "teamSetting"; setting: TeamSettingKey }
  | { source: "none" };

export interface GroupedConnection {
  label: string;
  route: string;
}

/** What interacting with the card does (navigate to a detail page, open the Gen III connect flow, etc.). */
export type CardAction =
  /**
   * `route` is the connected/manage page; `connectRoute` (when the connect and
   * settings surfaces differ) is where a not-yet-connected team is sent instead.
   */
  | { behavior: "settingsPage"; route: string; connectRoute?: string }
  | { behavior: "gen3Connect"; kind: IntegrationKind }
  | { behavior: "externalSurface"; route: string; surfaceLabel: string }
  | { behavior: "detailPanel" }
  /** Activation rows are the entire management surface; no panel below. */
  | { behavior: "activationOnly" }
  | { behavior: "contactRedo" }
  | { behavior: "readOnly" }
  | { behavior: "group"; connections: GroupedConnection[] };

export interface IntegrationCatalogEntry {
  /** Unique kebab-case id; stable — used in URLs, analytics, and logo lookup. */
  slug: string;
  name: string;
  description: string;
  products: readonly RedoProduct[];
  type: IntegrationTypeCategory;
  status: StatusSource;
  action: CardAction;
  /** ANY-of: the card renders if the user holds at least one. */
  permissions: readonly UserPermissions[];
  /**
   * Per-product row gate (ANY-of) overriding PRODUCT_SECTION_PERMISSIONS, for
   * rows whose legacy connect path required a different permission.
   */
  productPermissions?: Partial<Record<RedoProduct, readonly UserPermissions[]>>;
  /** Gen II discriminator kinds this entry accounts for (drift coverage + status RPC input). */
  gen2Kinds: readonly Gen2IntegrationKind[];
  /** Gen III IntegrationKind values this entry accounts for. */
  gen3Kinds: readonly IntegrationKind[];
  adminOnly?: boolean;
  featureFlag?: FeatureFlagKey;
  isBeta?: boolean;
  /** External docs page (developers.redo.com) surfaced as a link card on the vendor detail page. */
  docsUrl?: string;
  /** Docs for providers brokered through this integration without their own card (e.g. TrackStar WMSes). */
  providerDocs?: readonly { name: string; url: string }[];
}
